
import React, { useCallback, useMemo, useEffect } from 'react'
import { type NodeProps } from '@xyflow/react'
import { VideoFile } from '@/components/canvas/nodes/variants/VideoFile'
import type { GenieNodeState } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'

export interface VideoFileNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

function VideoFileNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as VideoFileNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)

  const handleFileChange = useCallback((fileUuid: string, metadata: Record<string, unknown>) => {
    onStateChange((prevState: Record<string, unknown>) => ({
      ...prevState,
      fileUuid,
      fileMetadata: metadata,
    }))
  }, [onStateChange])

  const handleSelect = useCallback(() => {}, [])

  // Programmatic action handler
  const action = genieState._action
  const actionTimestamp = action?.ts

  useEffect(() => {
    if (!action) return

    switch (action.type) {
      case 'clear':
        onStateChange(prev => ({ ...prev, fileUuid: undefined, fileMetadata: undefined }))
        break
      case 'update':
        if (action.params?.fileUuid !== undefined) {
          onStateChange(prev => ({
            ...prev,
            fileUuid: action.params.fileUuid,
            fileMetadata: action.params.fileMetadata,
          }))
        }
        break
    }

    onStateChange(prev => ({ ...prev, _action: null }))
  }, [actionTimestamp, action, onStateChange])

  const fileProps = useMemo(
    () => ({
      id,
      title: genieState.title,
      selected: selected || genieState.selected,
      fileUuid: genieState.fileUuid as string | undefined,
      canvasId: genieState.canvasId as string | undefined,
      hasParent: !!genieState.parentNodeId,
      onFileChange: handleFileChange,
      onSelect: handleSelect,
      onHoverChange,
      onStateChange,
      state: genieState,
    }),
    [id, genieState, selected, handleFileChange, handleSelect, onHoverChange, onStateChange],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.VideoFile

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <VideoFile {...fileProps} />
    </>
  )
}

export const VideoFileNode = React.memo(
  VideoFileNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as VideoFileNodeData
    const nextData = nextProps.data as unknown as VideoFileNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.fileUuid === nextData.state.fileUuid &&
      prevData.state.canvasId === nextData.state.canvasId &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.state.fileMetadata === nextData.state.fileMetadata &&
      prevData.state.generationStatus === nextData.state.generationStatus &&
      prevData.state.generationProgress === nextData.state.generationProgress &&
      prevData.state._action === nextData.state._action &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

VideoFileNode.displayName = 'VideoFileNode'
